import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { PrimaryTrade } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AddEditTradeModalComponent } from 'src/app/modals/add-edit-trade-modal/add-edit-trade-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-admin-admin-trades',
  templateUrl: './admin-admin-trades.component.html',
  styleUrl: './admin-admin-trades.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminAdminTradesComponent {
  constructor(
    private ModalService: NgbModal,
    private TradeService: TradeService,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private AuthService: AuthService
  ) {}

  suppliedGridQuery: GenericObject = {
    attributes: [
      'isTransporter',
      'isOnSite',
      'isNonProfessional',
      'isProfessional',
      'isProfessionalOrNonProfessional'
    ],
    aliasedAttributes: [
      'hasProfessionalTrade',
      'hasNonProfessionalTrade',
      'hasProfessionalOrNonProfessionalTrade'
    ]
  };

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'delete']
  };

  collapseTrades: boolean = false;
  collapseTradesRequirements: boolean = true;
  collapseTradeCategories: boolean = true;
  collapseTradeSpecialtyCategories: boolean = true;
  reloadGrid: boolean = true;
  LoggedInUser = this.AuthService.getUser();

  hasValidEditPermission() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      return role && new Set(['ADMIN_ADMIN', 'ADMIN_FRAUD_TEAM']).has(role);
    }

    return false;
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: PrimaryTrade;
  }) {
    const { vendorsPrimary, id } = rowData;

    if (action === 'edit') {
      this.addEditTrade(rowData);
    }

    if (action === 'delete') {
      if (vendorsPrimary && vendorsPrimary > 0) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot delete Trade. Trade is assigned as primary to vendors.',
          { returnError: true }
        );
      }

      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, backdrop: 'static', size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to delete this trade.'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.TradeService.deleteTrade(
              this.UtilityService.stringifyObjectsInQueryObject({ id })
            ).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted trade.'
                );
                this.reloadGrid = true;
              }
            });
          }
        }
      });
    }
  }

  addEditTrade(existingTrade?: PrimaryTrade) {
    const { componentInstance, result } = this.ModalService.open(
      AddEditTradeModalComponent,
      { centered: true, size: 'lg' }
    );
    const trade = existingTrade ? existingTrade : new PrimaryTrade({});

    Object.assign(componentInstance, { PrimaryTrade: trade });

    from(result).subscribe({
      next: () => {
        this.TradeService.saveTrade(trade).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully saved trade.'
            );
            this.reloadGrid = true;
          }
        });
      }
    });
  }

  addTrade() {
    this.addEditTrade();
  }
}
